import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useSendTaskEvent, useTasks } from "../api";
import { ErrorState } from "../components/ui/AsyncState";
import { Page } from "../components/ui/Page";
import { stageVariants } from "./task-reveal/motion";
import { PreTaskCard } from "./task-reveal/PreTaskCard";
import {
  EmptyTaskMessage,
  ErrorTaskMessage,
  LoadingTaskCard,
  StartingTaskCard,
} from "./task-reveal/TaskRevealMessages";
import { TaskResult } from "./task-reveal/TaskRevealResult";
import { useTaskRevealLaunch } from "./task-reveal/useTaskRevealLaunch";
import { TaskLaunchForm } from "./TaskDetailPage";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

type TurnstileRenderOptions = {
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  sitekey: string;
  theme?: "light" | "dark" | "auto";
};

type TurnstileApi = {
  ready: (callback: () => void) => void;
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type ViewState =
  | "loading"
  | "starting"
  | "pretask"
  | "task"
  | "empty"
  | "error";

export function TaskRevealPage({ loading }: { loading: boolean }) {
  const shouldReduceMotion = useReducedMotion();
  const [rejectedTaskIds, setRejectedTaskIds] = useState<string[]>([]);
  const [forceRefreshKey, setForceRefreshKey] = useState(0);
  const enableRefetch = useRef(true);
  const turnstileContainerId = useId();
  const turnstileWidgetId = useRef<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const { data, isLoading, isRefetching, isError, error } = useTasks(
    rejectedTaskIds,
    forceRefreshKey,
    enableRefetch.current,
  );
  const sendTaskEvent = useSendTaskEvent();
  const tasks = useMemo(() => data ?? [], [data]);
  const task = useMemo(() => tasks[0] ?? null, [tasks]);
  useEffect(() => {
    if (!task) return;
    sendTaskEvent.mutate({ event: "seen", taskId: task.uid });
  }, [task]);
  const checkAgain = () => {
    setForceRefreshKey((current) => current + 1);
  };
  const resetTasks = () => {
    setRejectedTaskIds([]);
    setForceRefreshKey((current) => current + 1);
  };
  const getNextTask = () => {
    enableRefetch.current = false;
    console.log("getting next task", { data, forceRefreshKey });
    if (!data || data.length <= 1) {
      setForceRefreshKey((current) => current + 1);
    }

    sendTaskEvent.mutate({ event: "skipped", taskId: task.uid });
    setRejectedTaskIds((current) => [...current, task.uid]);
    enableRefetch.current = true;
  };
  const {
    completeLaunch,
    hasPendingPreTask,
    isSubmittingLaunch,
    launchError,
    launchFormRef,
    launchTask,
    panelistQuery,
    preTaskQuery,
    preTaskQuestions,
    resetLaunch,
  } = useTaskRevealLaunch();
  useEffect(() => {
    resetLaunch();
  }, []);

  const isStarting = isSubmittingLaunch;

  const debugState: ViewState | "" = "";
  const viewState: ViewState =
    debugState ||
    (isLoading || isRefetching || loading
      ? "loading"
      : isError
        ? "error"
        : isStarting
          ? "starting"
          : hasPendingPreTask && !preTaskQuery.isLoading
            ? "pretask"
            : task
              ? "task"
              : "empty");

  useEffect(() => {
    if (!turnstileSiteKey || !task) {
      return;
    }

    const container = document.getElementById(turnstileContainerId);
    const api = window.turnstile;
    if (!container || !api || turnstileWidgetId.current) {
      return;
    }

    api.ready(() => {
      if (turnstileWidgetId.current) {
        return;
      }
      turnstileWidgetId.current = api.render(container, {
        callback: (token) => {
          setTurnstileToken(token);
          setTurnstileError(null);
        },
        "error-callback": () => {
          setTurnstileToken("");
          setTurnstileError(
            "Bot protection could not be verified. Please try again.",
          );
        },
        "expired-callback": () => {
          setTurnstileToken("");
          setTurnstileError(
            "Bot protection expired. Please complete it again.",
          );
          if (turnstileWidgetId.current) {
            window.turnstile?.reset(turnstileWidgetId.current);
          }
        },
        sitekey: turnstileSiteKey,
        theme: "light",
      });
    });

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [task, turnstileContainerId]);

  const canLaunch = !turnstileSiteKey || Boolean(turnstileToken);

  const balanceAfter =
    task && panelistQuery.data ? panelistQuery.data.points + task.points : null;
  return (
    <>
      <Page
        className="task-reveal-page"
        description="Discover the next best task for your day."
        title="Task reveal"
      >
        <div className="task-reveal-stage">
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              animate="animate"
              className="task-reveal-shell"
              exit="exit"
              initial="initial"
              key={
                viewState === "task" && task
                  ? `task-${task.uid}`
                  : viewState === "pretask" && task
                    ? `pretask-${task.uid}`
                    : viewState
              }
              variants={stageVariants}
            >
              {viewState === "loading" ? (
                <LoadingTaskCard
                  shouldReduceMotion={Boolean(shouldReduceMotion)}
                />
              ) : viewState === "starting" ? (
                <StartingTaskCard
                  shouldReduceMotion={Boolean(shouldReduceMotion)}
                />
              ) : viewState === "pretask" ? (
                <PreTaskCard
                  onCancel={resetLaunch}
                  onSubmit={(answers) =>
                    completeLaunch(answers, turnstileToken)
                  }
                  questions={preTaskQuestions}
                />
              ) : viewState === "task" && task ? (
                <>
                  <TaskResult
                    balanceAfter={balanceAfter}
                    canLaunch={canLaunch}
                    isLaunching={isStarting || preTaskQuery.isLoading}
                    onLaunch={() => {
                      if (turnstileSiteKey && !turnstileToken) {
                        setTurnstileError(
                          "Please complete the bot protection check.",
                        );
                        return;
                      }
                      launchTask(task);
                      sendTaskEvent.mutate({
                        event: "selected",
                        taskId: task.uid,
                      });
                    }}
                    onReject={() => {
                      resetLaunch();
                      getNextTask();
                    }}
                    task={task}
                  />
                  {turnstileSiteKey && (
                    <>
                      <div
                        className="signup-turnstile"
                        id={turnstileContainerId}
                      />
                      {turnstileError && (
                        <div className="err-msg signup-turnstile-error">
                          {turnstileError}
                        </div>
                      )}
                    </>
                  )}
                  {(launchError ||
                    preTaskQuery.error ||
                    panelistQuery.error) && (
                    <div style={{ marginTop: 16 }}>
                      <ErrorState
                        error={
                          launchError ??
                          preTaskQuery.error ??
                          panelistQuery.error
                        }
                        title="Could not launch survey"
                      />
                    </div>
                  )}
                </>
              ) : viewState === "empty" ? (
                <EmptyTaskMessage
                  onAction={(reset) => (reset ? resetTasks() : checkAgain())}
                />
              ) : viewState === "error" ? (
                <ErrorTaskMessage error={error} onAction={() => checkAgain()} />
              ) : null}
            </motion.div>
          </AnimatePresence>
        </div>
      </Page>
      <TaskLaunchForm ref={launchFormRef} />
    </>
  );
}

export { TaskRevealPage as TaskReveal };
